// Logica pura del mapa de la flota (PP-51): que vehiculos entran segun el
// filtro de viaje y el de planta, en que orden se listan, cuanto hace de su
// posicion y que encuadre los muestra a todos. Sin React ni reloj: `now` entra
// por parametro. El copy vive en los componentes.
import type { ProviderFleetPositionsResponse, ProviderFleetVehicle } from "@ucorp/contracts";

/** "Con viaje" (en curso o asignado hoy) o "Toda la flota" con posicion vigente. */
export type FleetFilter = "with_trip" | "all";

/** Clave estable de un vehiculo en el mapa y la lista: el codigo de movil es unico en la nomina. */
export function vehicleKey(vehicle: Pick<ProviderFleetVehicle, "code">): string {
  return vehicle.code;
}

/**
 * Planta en la que se cuenta el vehiculo para el filtro global: la de su
 * viaje vigente; sin viaje, la de su ultima posicion.
 */
export function vehicleSiteId(vehicle: ProviderFleetVehicle): string {
  return vehicle.trip?.siteId ?? vehicle.position.siteId;
}

/**
 * Vehiculos a mostrar. `siteIds` son las plantas en alcance (todas las de la
 * cuenta o la filtrada); un vehiculo de una planta que la cuenta no tiene no
 * entra aunque la API lo mande (misma defensa que el Inicio, logic/home.ts).
 */
export function vehiclesInScope(
  vehicles: readonly ProviderFleetVehicle[],
  filter: FleetFilter,
  siteIds: ReadonlySet<string>,
): ProviderFleetVehicle[] {
  return vehicles.filter((v) => siteIds.has(vehicleSiteId(v)) && (filter === "all" || v.trip !== undefined));
}

const STATUS_ORDER = { in_progress: 0, scheduled: 1 } as const;

/** Orden de la lista: en curso, despues asignados, despues sin viaje; dentro, por codigo. */
export function sortForList(vehicles: readonly ProviderFleetVehicle[]): ProviderFleetVehicle[] {
  const rank = (v: ProviderFleetVehicle) => (v.trip === undefined ? 2 : STATUS_ORDER[v.trip.status]);
  return [...vehicles].sort((a, b) => rank(a) - rank(b) || a.code.localeCompare(b.code, "es", { numeric: true }));
}

/** Minutos enteros desde la posicion (0 si es de este minuto o del futuro por reloj corrido). */
export function minutesSince(instant: string, now: number): number {
  return Math.max(0, Math.floor((now - Date.parse(instant)) / 60_000));
}

/** Zona IANA de una planta de la respuesta; las horas operativas se muestran en ella (regla 9). */
export function siteTimezone(response: Pick<ProviderFleetPositionsResponse, "sites">, siteId: string): string | undefined {
  return response.sites.find((site) => site.siteId === siteId)?.timezone;
}

export interface Bounds {
  readonly latMin: number;
  readonly latMax: number;
  readonly lngMin: number;
  readonly lngMax: number;
}

/** Recuadro que contiene a todos los vehiculos; `null` sin vehiculos. */
export function fleetBounds(vehicles: readonly Pick<ProviderFleetVehicle, "position">[]): Bounds | null {
  if (vehicles.length === 0) return null;
  let latMin = Infinity, latMax = -Infinity, lngMin = Infinity, lngMax = -Infinity;
  for (const { position } of vehicles) {
    const { lat, lng } = position.coordinates;
    latMin = Math.min(latMin, lat); latMax = Math.max(latMax, lat);
    lngMin = Math.min(lngMin, lng); lngMax = Math.max(lngMax, lng);
  }
  return { latMin, latMax, lngMin, lngMax };
}

/** Centro del recuadro: el ancla del encuadre (initialCamera de @ucorp/web-map). */
export function boundsCenter(bounds: Bounds): { lat: number; lng: number } {
  return { lat: (bounds.latMin + bounds.latMax) / 2, lng: (bounds.lngMin + bounds.lngMax) / 2 };
}
